import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { extractIdFromSlug } from '../utils/slugUtils';
import { 
  PuzzlePieceIcon, 
  CalendarIcon,
  TagIcon,
  ArrowLeftIcon,
  PlayIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import publicMatchService from '../services/publicMatchService';
import copyService from '../services/copyService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const PublicMatchDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  
  const [match, setMatch] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    const fetchMatchData = async () => {
      if (!slug) return;
      
      const id = extractIdFromSlug(slug);
      
      setLoading(true);
      setError(null);
      
      try {
        const matchData = await publicMatchService.getMatchById(id);
        setMatch(matchData);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load match details');
      } finally {
        setLoading(false);
      }
    };

    fetchMatchData();
  }, [slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const handleStartMatch = async () => {
    if (user && slug) {
      const id = extractIdFromSlug(slug);
      setCopying(true);
      try {
        const result = await copyService.copyMatch(id);
        toast.success(result.message);
        navigate('/matches');
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Erro ao copiar jogo');
      } finally {
        setCopying(false);
      }
    } else {
      navigate(`/register?redirect=/matches/${slug}/play`);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${
        isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <PuzzlePieceIcon className="h-16 w-16 text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Jogo não encontrado</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {error || 'O jogo que você está procurando não existe ou foi removido.'}
        </p>
        <Link
          to="/search"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Voltar para pesquisa
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* Enhanced Title & Description */}
        <title>{`${match.title || 'Jogo de Memória'} - ${match.pairs?.length || 0} Pares | Jogo de Memória Online Grátis | Upmindy`}</title>
        <meta name="description" content={`${match.description || `Jogo de memória com ${match.pairs?.length || 0} pares`} | Jogo online grátis no Upmindy.`} />
        
        {/* Keywords & SEO */}
        <meta name="keywords" content={match.tags?.join(', ') || 'jogo de memória, match, aprendizado, educação, pares'} />
        <meta name="author" content={match.creatorName || 'Upmindy'} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta httpEquiv="Content-Language" content="pt-BR" />
        <meta name="theme-color" content="#3B82F6" />
        
        {/* Canonical URLs */}
        <link rel="canonical" href={`https://upmindy.com/matches/${slug}`} />
        <link rel="alternate" hrefLang="pt-BR" href={`https://upmindy.com/matches/${slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://upmindy.com/matches/${slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={`${match.title || 'Jogo de Memória'} - Jogo de Memória | Upmindy`} />
        <meta property="og:description" content={match.description || `${match.pairs?.length || 0} pares para treinar sua memória`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://upmindy.com/matches/${slug}`} />
        <meta property="og:image" content="https://upmindy.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Upmindy" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="article:published_time" content={match.createdAt} />
        <meta property="article:modified_time" content={match.updatedAt} />
        <meta property="article:author" content={match.creatorName || 'Upmindy'} />
        <meta property="article:section" content="Education" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@upmindy_app" />
        <meta name="twitter:creator" content="@upmindy_app" />
        <meta name="twitter:title" content={`${match.title || 'Jogo de Memória'} - Jogo de Memória | Upmindy`} />
        <meta name="twitter:description" content={match.description || `${match.pairs?.length || 0} pares para treinar sua memória`} />
        <meta name="twitter:image" content="https://upmindy.com/og-image.png" />
        <meta name="twitter:image:alt" content={`Jogo de Memória: ${match.title || 'Upmindy'}`} />
        <meta name="twitter:label1" content="Pares" />
        <meta name="twitter:data1" content={`${match.pairs?.length || 0} pares`} />
        <meta name="twitter:label2" content="Tipo" />
        <meta name="twitter:data2" content="Jogo de Memória" />
        
        {/* Structured Data - Game/LearningResource Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["Game", "LearningResource"],
            "name": match.title || "Jogo de Memória",
            "description": match.description || `Jogo de memória com ${match.pairs?.length || 0} pares`,
            "gameMode": "SinglePlayer",
            "learningResourceType": "Memory Game",
            "educationalLevel": "intermediate",
            "inLanguage": "pt-BR",
            "isAccessibleForFree": true,
            "creator": {
              "@type": "Person",
              "name": match.creatorName || "Upmindy"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Upmindy",
              "url": "https://upmindy.com"
            },
            "datePublished": match.createdAt,
            "dateModified": match.updatedAt,
            "url": `https://upmindy.com/matches/${slug}`,
            "keywords": match.tags?.join(', ') || 'jogo de memória, match'
          })}
        </script>
        
        {/* Structured Data - Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Início",
                "item": "https://upmindy.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Explorar",
                "item": "https://upmindy.com/search"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": match.title || "Jogo de Memória",
                "item": `https://upmindy.com/matches/${slug}`
              }
            ]
          })}
        </script>
        
        {/* Structured Data - FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `Quantos pares tem o jogo ${match.title || 'este'}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `Este jogo contém ${match.pairs?.length || 0} pares cuidadosamente elaborados.`
                }
              },
              {
                "@type": "Question",
                "name": "Posso jogar este jogo gratuitamente?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sim! Este jogo está disponível gratuitamente no Upmindy."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Breadcrumbs */}
            <nav className="flex mb-6" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  <Link
                    to="/"
                    className={`inline-flex items-center text-sm font-medium ${
                      isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Início
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <Link
                      to="/search"
                      className={`ml-1 text-sm font-medium md:ml-2 ${
                        isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Explorar
                    </Link>
                  </div>
                </li>
                <li aria-current="page">
                  <div className="flex items-center">
                    <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className={`ml-1 text-sm font-medium md:ml-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      {match.title.length > 30 ? `${match.title.substring(0, 30)}...` : match.title}
                    </span>
                  </div>
                </li>
              </ol>
            </nav>

            <div className="flex items-start space-x-4">
              <PuzzlePieceIcon className="h-12 w-12 sm:h-16 sm:w-16 text-purple-600 flex-shrink-0" />
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">{match.title}</h1>
                {match.description && (
                  <p className={`text-base sm:text-lg mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {match.description}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center">
                    <PuzzlePieceIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <span>{match.pairs?.length || 0} pares</span>
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <span>{formatDate(match.createdAt)}</span>
                  </div>
                  <div className="flex items-center">
                    <PuzzlePieceIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <span>Criado por {match.creatorName || 'Upmindy'}</span>
                  </div>
                </div>

                {match.tags && match.tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {match.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                          isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleStartMatch}
                    disabled={copying}
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {copying ? (
                      'Copiando...'
                    ) : (
                      <>
                        <PlayIcon className="h-5 w-5 mr-2" />
                        {user ? 'Copiar e Jogar' : 'Começar Jogo'}
                      </>
                    )}
                  </button>
                  
                  <Link
                    to="/search"
                    className={`flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg border transition-colors font-medium ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-800'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    Explorar Mais
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pairs Preview */}
        {match.pairs && match.pairs.length > 0 && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-xl font-bold mb-4">Pares do Jogo</h2>
            <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm mb-4`}>
              Este jogo contém {match.pairs.length} {match.pairs.length === 1 ? 'par' : 'pares'}. 
              {user ? ' Copie para sua coleção para começar!' : ' Faça login para começar!'}
            </div>
            <div className="grid gap-4">
              {match.pairs.slice(0, 5).map((pair: any, index: number) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs opacity-75 mb-1">Frente</div>
                      <div className="font-medium">{pair.front}</div>
                    </div>
                    <div>
                      <div className="text-xs opacity-75 mb-1">Verso</div>
                      <div className="font-medium">{pair.back}</div>
                    </div>
                  </div>
                </div>
              ))}
              {match.pairs.length > 5 && (
                <div className={`text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  +{match.pairs.length - 5} pares adicionais
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PublicMatchDetail;
